import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  EVENTS,
  Joyride,
  STATUS,
  type EventData,
  type Step,
} from "react-joyride";
import { useLingui } from "@lingui/macro";
import API from "@app/services/api";
import { usePreviewView } from "@app/zustand/preview-view";
import { useScope } from "@app/zustand/scope";
import type { PathwayPatientRow } from "@app/types/dashboard-pathway";
import {
  findAnchor,
  reveal,
  wait,
  waitForAnchor,
  waitForQuiet,
} from "./anchors";
import { useDemoTour } from "./demo-tour-store";
import TourCard, { TourLoader, type TourCardData } from "./TourCard";
import TourHalo from "./TourHalo";
import {
  TOUR_CHAPTERS,
  TOUR_STOPS,
  type TourChapterId,
  type TourPersona,
  type TourPlace,
  type TourStop,
} from "./tour-script";

/** A new key for a new tour: a visitor who finished the first one has not seen this one. */
const COMPLETED_KEY = "mage-x-demo-tour-v2-completed";

/** How long a stop waits for its page to draw the anchor — a patient page reads several endpoints. */
const ANCHOR_WAIT_MS = 10_000;

/** The banner's « Visite guidée » button. */
const OUTRO_ANCHORS = ["demo-tour"];

function hasCompleted(): boolean {
  try {
    return window.localStorage.getItem(COMPLETED_KEY) === "1";
  } catch {
    return false;
  }
}

function markCompleted() {
  try {
    window.localStorage.setItem(COMPLETED_KEY, "1");
  } catch {
    // Storage refused (private window): the tour offers itself again next visit, nothing worse.
  }
}

/** Puts the demo in the persona a stop is told in, as the banner's toggle does. True if it moved. */
function applyPersona(persona: TourPersona): boolean {
  const { previewRole, setPreview } = usePreviewView.getState();
  const { scope, setScope } = useScope.getState();
  const wantedScope = persona === "global" ? "specialite" : "mine";
  const moved = previewRole !== persona || scope !== wantedScope;
  if (previewRole !== persona) setPreview({ role: persona });
  if (scope !== wantedScope) setScope(wantedScope);
  return moved;
}

/** The richest dossier: most episodes, then one whose latest episode records an act. */
function pickShowcase(rows: PathwayPatientRow[]): PathwayPatientRow | null {
  let best: PathwayPatientRow | null = null;
  for (const row of rows) {
    if (
      !best ||
      row.episodes > best.episodes ||
      (row.episodes === best.episodes && !!row.act && !best.act)
    ) {
      best = row;
    }
  }
  return best;
}

/** The laboratory's worked example: a dataset, and the first and last experiments run on it. */
interface LabExample {
  dataset: string;
  baseline: string;
  latest: string;
}

interface ExperimentRow {
  id: string;
  status: string;
  created_at: string;
}

/** The first dataset with two completed experiments: the one a fix was measured on. */
async function findLabExample(): Promise<LabExample | null> {
  const res = await API.get({ path: "/dataset/" });
  const datasets: { id: string }[] =
    res?.ok && Array.isArray(res.data?.datasets) ? res.data.datasets : [];
  for (const dataset of datasets) {
    const runs = await API.get({ path: `/dataset/${dataset.id}/experiments` });
    const rows: ExperimentRow[] =
      runs?.ok && Array.isArray(runs.data?.experiments)
        ? runs.data.experiments
        : [];
    const done = rows
      .filter((row) => row.status === "COMPLETED")
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    if (done.length >= 2) {
      return {
        dataset: dataset.id,
        baseline: done[0].id,
        latest: done[done.length - 1].id,
      };
    }
  }
  return null;
}

/** Where a laboratory stop is told, or null when the demo holds no worked example. */
function labPath(place: TourPlace, example: LabExample | null): string | null {
  if (!example) return null;
  const dataset = `/datasets/${example.dataset}`;
  if (place === "lab-dataset") return dataset;
  if (place === "lab-experiment")
    return `${dataset}/experiments/${example.baseline}?tab=metrics`;
  if (place === "lab-compare")
    return `${dataset}/experiments/compare?ids=${example.baseline},${example.latest}`;
  return null;
}

function anchorsOf(stepId: string | null): string[] | null {
  if (stepId === "outro") return OUTRO_ANCHORS;
  return TOUR_STOPS.find((tourStop) => tourStop.id === stepId)?.anchors ?? null;
}

/**
 * The demo's guided tour: how the platform works, told on the platform itself.
 *
 * Each stop brings its own page up before it is shown — persona, route, the showcase dossier — and
 * waits for its anchor. An anchor that never draws is told in the middle of the screen: a visitor
 * never loses a stop because a page was slow, which is how the first tour lost two of its five.
 */
export default function DemoTour() {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const running = useDemoTour((state) => state.running);
  const start = useDemoTour((state) => state.start);
  const finish = useDemoTour((state) => state.stop);
  /** Steps whose anchor never drew: told in the middle of the screen. */
  const [centred, setCentred] = useState<ReadonlySet<string>>(() => new Set());
  /** The step whose card is on screen, for the halo. */
  const [shownStepId, setShownStepId] = useState<string | null>(null);
  const navigateRef = useRef(navigate);
  const showcase = useRef<{ locale: string; path: string } | null>(null);
  const laboratory = useRef<{
    locale: string;
    example: LabExample | null;
  } | null>(null);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // First visit: the tour offers itself once the dashboard has had time to draw.
  useEffect(() => {
    if (hasCompleted()) return;
    const timer = window.setTimeout(start, 1500);
    return () => window.clearTimeout(timer);
  }, [start]);

  const showcasePath = useCallback(async (): Promise<string | null> => {
    const locale = i18n.locale;
    if (showcase.current?.locale === locale) return showcase.current.path;
    const res = await API.get({
      path: "/dashboard/pathway/patients",
      query: { scope: "mine" },
    });
    const rows: PathwayPatientRow[] =
      res?.ok && Array.isArray(res.data?.patients) ? res.data.patients : [];
    const best = pickShowcase(rows);
    if (!best) return null;
    const path = `/patient/${best.tenant_id}/${best.id_patient}`;
    showcase.current = { locale, path };
    return path;
  }, [i18n]);

  /** The demo lists the dataset of its locale, so the example is resolved per locale too. */
  const labExample = useCallback(async (): Promise<LabExample | null> => {
    const locale = i18n.locale;
    if (laboratory.current?.locale === locale)
      return laboratory.current.example;
    const example = await findLabExample();
    laboratory.current = { locale, example };
    return example;
  }, [i18n]);

  /**
   * Waits for a step's anchor and brings it into view, or centres the step when none draws.
   * `moved`: the page or the persona just changed, so what the anchor frames is still loading.
   */
  const settle = useCallback(
    async (id: string, anchors: string[], waitMs: number, moved: boolean) => {
      let anchor = await waitForAnchor(anchors, waitMs);
      if (anchor && moved) {
        await waitForQuiet();
        anchor = findAnchor(anchors);
      }
      if (anchor) await reveal(anchor);
      const found = !!anchor;
      setCentred((previous) => {
        if (previous.has(id) === !found) return previous;
        const next = new Set(previous);
        if (found) next.delete(id);
        else next.add(id);
        return next;
      });
      // Let the steps re-render before Joyride reads this one's target.
      await wait(50);
    },
    [],
  );

  const prepare = useCallback(
    async (tourStop: TourStop) => {
      let moved = applyPersona(tourStop.persona);
      const inLab = tourStop.place.startsWith("lab-");
      let destination: string | null = null;
      if (tourStop.place === "dashboard") destination = "/dashboard";
      else if (tourStop.place === "catalogue") destination = "/catalogue";
      else if (tourStop.place === "patient") destination = await showcasePath();
      else if (tourStop.place === "lab-corrections")
        destination = "/laboratoire/corrections";
      else if (inLab) destination = labPath(tourStop.place, await labExample());
      // A laboratory stop names its tab or its experiments in the query: it is part of the place.
      const here = destination?.includes("?")
        ? window.location.pathname + window.location.search
        : window.location.pathname;
      if (destination && here !== destination) {
        navigateRef.current(destination);
        moved = true;
      }
      // No dossier or no worked example to open means no anchor will ever draw: centre the stop
      // at once.
      const hopeless =
        (tourStop.place === "patient" &&
          !destination &&
          !window.location.pathname.startsWith("/patient/")) ||
        (inLab && !destination);
      await settle(
        tourStop.id,
        tourStop.anchors,
        hopeless ? 0 : ANCHOR_WAIT_MS,
        moved,
      );
    },
    [settle, showcasePath, labExample],
  );

  const steps = useMemo<Step[]>(() => {
    const chapterStarts = Object.fromEntries(
      TOUR_CHAPTERS.map((chapter) => [
        chapter.id,
        1 + TOUR_STOPS.findIndex((tourStop) => tourStop.chapter === chapter.id),
      ]),
    ) as Record<TourChapterId, number>;
    const intro: TourCardData = { kind: "intro", chapterStarts };
    const outro: TourCardData = { kind: "outro" };
    return [
      {
        id: "intro",
        target: "body",
        placement: "center",
        content: null,
        data: intro,
      },
      ...TOUR_STOPS.map((tourStop, index): Step => {
        const inCentre = centred.has(tourStop.id);
        const data: TourCardData = {
          kind: "stop",
          stopId: tourStop.id,
          number: index + 1,
          total: TOUR_STOPS.length,
        };
        return {
          id: tourStop.id,
          target: inCentre ? "body" : () => findAnchor(tourStop.anchors),
          placement: inCentre ? "center" : tourStop.placement,
          title: t(tourStop.title),
          content: t(tourStop.body),
          before: () => prepare(tourStop),
          data,
        };
      }),
      // The last card points at the button that brings the tour back.
      {
        id: "outro",
        target: centred.has("outro") ? "body" : () => findAnchor(OUTRO_ANCHORS),
        placement: centred.has("outro") ? "center" : "bottom-end",
        content: null,
        before: () => settle("outro", OUTRO_ANCHORS, 2_000, false),
        data: outro,
      },
    ];
  }, [centred, t, prepare, settle]);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (data.type === EVENTS.TOOLTIP)
        setShownStepId(String(data.step.id ?? ""));
      else if (data.type === EVENTS.STEP_AFTER) setShownStepId(null);
      if (data.status !== STATUS.FINISHED && data.status !== STATUS.SKIPPED)
        return;
      markCompleted();
      finish();
      setShownStepId(null);
      setCentred((previous) => (previous.size ? new Set() : previous));
    },
    [finish],
  );

  const haloAnchors =
    running && shownStepId && !centred.has(shownStepId)
      ? anchorsOf(shownStepId)
      : null;

  return (
    <>
      {haloAnchors && <TourHalo anchors={haloAnchors} />}
      <Joyride
        steps={steps}
        run={running}
        continuous
        initialStepIndex={0}
        onEvent={handleEvent}
        tooltipComponent={TourCard}
        loaderComponent={TourLoader}
        locale={{
          back: t`Précédent`,
          close: t`Fermer la visite`,
          last: t`Terminer`,
          next: t`Suivant`,
          nextWithProgress: t`Suivant`,
          open: t`Ouvrir la visite`,
          skip: t`Passer la visite`,
        }}
        options={{
          arrowBase: 20,
          arrowColor: "#ffffff",
          arrowSize: 10,
          backgroundColor: "#ffffff",
          beforeTimeout: ANCHOR_WAIT_MS + 8_000,
          blockTargetInteraction: true,
          closeButtonAction: "skip",
          dismissKeyAction: false,
          loaderDelay: 600,
          offset: 6,
          overlayClickAction: false,
          overlayColor: "rgba(15, 23, 42, 0.55)",
          primaryColor: "#1752B7",
          skipBeacon: true,
          skipScroll: true,
          spotlightPadding: 8,
          spotlightRadius: 12,
          textColor: "#0f172a",
        }}
        styles={{
          floater: { filter: "none" },
          loader: { height: "auto", width: "auto" },
        }}
      />
    </>
  );
}
